import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, Observable, of, startWith, switchMap } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { AuthorizedApiService } from '../../services/authorized-api.service';
import { BookingInfo } from '../../models/Booking';
import { LoadingSpinnerComponent } from '../ui/loading-spinner-component';
import { SeparatorComponent } from '../ui/separator.component';
import { BorderIconButtonComponent } from '../buttons/border-icon-button.component';
import MoneyUtils from '../../utils/Utils';

interface BookingVM {
  loading: boolean;
  booking: BookingInfo | undefined;
  error: string | undefined;
}

@Component({
  selector: 'app-booking-view',
  standalone: true,
  imports: [
    CommonModule,
    LoadingSpinnerComponent,
    SeparatorComponent,
    BorderIconButtonComponent,
  ],
  template: `
    <div class="max-w-lg mx-auto flex-col pt-20 text-theme-text">
      <div class="flex-1 bg-theme-surface rounded-t-3xl min-h-[calc(100vh-5rem)]">

        <!-- Header -->
        <div class="flex flex-row pt-6 px-4 pb-4 items-center justify-center">
          <div class="text-3xl break-words text-center">{{ title }}</div>
        </div>

        @if (vm$ | async; as vm) {

          @if (vm.loading) {
            <div class="flex justify-center mt-20">
              <app-loading-spinner size="xl"></app-loading-spinner>
            </div>
          }

          @if (vm.error) {
            <div class="px-8 mt-10 text-theme-text2 text-center">
              <i class="fas fa-circle-exclamation text-2xl mb-3"></i>
              <div class="text-lg font-medium">{{ vm.error }}</div>
            </div>
          }

          @if (vm.booking; as booking) {
            <div class="px-8">

              <!-- Booking details panel -->
              <div class="bg-theme-surface2 rounded-xl p-4 mt-2">
                <div class="flex flex-col gap-2">
                  @if (booking.is_prebooking && booking.external_ref) {
                    <!-- The guest knows the number from the source booking system;
                         the internal ref is what staff and receipts use -->
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">Bokningsnummer</div>
                      <div class="text-sm font-medium">{{ booking.external_ref }}</div>
                    </div>
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">Referens</div>
                      <div class="text-sm font-medium">{{ booking.booking_ref }}</div>
                    </div>
                  } @else {
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">Bokningsnummer</div>
                      <div class="text-sm font-medium">{{ booking.booking_ref }}</div>
                    </div>
                  }

                  @if (booking.pickup_date) {
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">{{ booking.is_prebooking ? 'Bokningstid' : 'Upphämtningstid' }}</div>
                      <div class="text-sm font-medium">{{ formatDate(booking.pickup_date) }}</div>
                    </div>
                  }

                  @if (booking.pax) {
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">Antal gäster</div>
                      <div class="text-sm font-medium">{{ booking.pax }}</div>
                    </div>
                  }

                  @if (booking.user_name) {
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">Namn</div>
                      <div class="text-sm font-medium">{{ booking.user_name }}</div>
                    </div>
                  }

                  @if (booking.phone) {
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">Telefon</div>
                      <div class="text-sm font-medium">{{ booking.phone }}</div>
                    </div>
                  }

                  @if (booking.collected_data) {
                    @for (entry of getCollectedDataEntries(booking); track entry.key) {
                      <div class="flex justify-between">
                        <div class="text-sm text-theme-text2">{{ entry.name }}</div>
                        <div class="text-sm font-medium">{{ entry.value }}</div>
                      </div>
                    }
                  }

                  @if (booking.note) {
                    <div class="flex justify-between">
                      <div class="text-sm text-theme-text2">Notering</div>
                      <div class="text-sm font-medium">{{ booking.note }}</div>
                    </div>
                  }

                  <div class="flex justify-between items-center">
                    <div class="text-sm text-theme-text2">Betalstatus</div>
                    <div class="flex items-center gap-2">
                      <div class="text-sm font-medium flex items-center gap-1" [ngClass]="getPaymentStatusColor(booking)">
                        @if (getPaymentStatus(booking) === 'Betald') {
                          <i class="fas fa-circle-check"></i>
                        }
                        {{ getPaymentStatus(booking) }}
                      </div>
                      @if (getPaymentStatus(booking) !== 'Betald' && !booking.pay_at_venue) {
                        <button (click)="payBooking(booking)" class="text-sm font-bold text-theme-primary px-3 py-1 rounded-lg border border-theme-primary">
                          Betala
                        </button>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <!-- Items -->
              <div class="mt-6">
                @for (item of getDisplayItems(booking); track $index) {
                  <div class="py-2">
                    <div class="flex items-center justify-between">
                      <div class="text-base text-theme-text font-bold">
                        @if (item.count > 1) {<span>{{item.count}}x </span>}
                        <span class="break-words">{{item.name}}</span>
                      </div>
                      <div class="ml-2 whitespace-nowrap tabular-nums">{{ getItemPrice(item) }}</div>
                    </div>
                    @if (item.customized) {
                      <div class="text-sm text-theme-text3 mt-0.5">{{ item.customized }}</div>
                    }
                    @if (item.comment) {
                      <div class="text-sm text-theme-text3">{{ item.comment }}</div>
                    }
                  </div>
                }

                <!-- Fallback to hints if no order_items -->
                @if (!booking.order_items || booking.order_items.length === 0) {
                  @if (booking.order_items_hint) {
                    @for (hint of booking.order_items_hint; track $index) {
                      <div class="py-2">
                        <div class="text-base font-bold break-words">{{ hint.name }}</div>
                        @if (hint.com) {
                          <div class="text-sm text-theme-text3">{{ hint.com }}</div>
                        }
                      </div>
                    }
                  }
                }
              </div>

              <app-separator></app-separator>

              <!-- Total -->
              <div class="flex justify-between mt-4">
                <div class="text-lg text-theme-text2 font-bold">Summa</div>
                <div class="text-lg tabular-nums font-bold">{{ moneyUtils.formatCent(booking.amount_cent, true) }}</div>
              </div>

              <!-- Receipt links -->
              @if (booking.receipt_keys.length > 0) {
                <div class="mt-8 flex flex-col gap-3">
                  @for (key of booking.receipt_keys; track key; let i = $index) {
                    <app-border-icon-button
                      icon="fa-receipt"
                      [text]="booking.receipt_keys.length === 1 ? 'Visa kvitto' : 'Kvitto ' + (i + 1)"
                      (onClick)="openReceipt(key)">
                    </app-border-icon-button>
                  }
                </div>
              }

              <div class="h-12"></div>
            </div>
          }
        }

      </div>
    </div>
  `
})
export class BookingViewComponent implements OnInit {
  vm$!: Observable<BookingVM>;
  venueId = '';
  title = 'Beställning';
  moneyUtils = MoneyUtils;

  constructor(
    private api: ApiService,
    private authorizedApi: AuthorizedApiService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.vm$ = this.route.paramMap.pipe(
      switchMap(pm => {
        this.venueId = pm.get('venue_id') || '';
        const bookingId = pm.get('booking_id');
        if (!this.venueId || !bookingId) {
          return of({ loading: false, booking: undefined, error: 'Bokning ej angiven' });
        }
        return this.api.getBookingInfo(this.venueId, bookingId).pipe(
          map(res => {
            this.title = res.booking?.is_prebooking ? 'Förbokning' : 'Beställning';
            return { loading: false, booking: res.booking, error: undefined };
          }),
          startWith({ loading: true, booking: undefined, error: undefined }),
          catchError(() => of({ loading: false, booking: undefined, error: 'Kunde inte hämta bokning' }))
        );
      })
    );
  }

  getDisplayItems(booking: BookingInfo) {
    if (!booking.order_items) return [];
    return booking.order_items.filter(item =>
      !(item.package && item.package.aggregated_cent == null)
    );
  }

  getItemPrice(item: any): string {
    // Server-grouped rows carry the formatted row total (count included)
    if (typeof item.formatted_item_total === 'string') {
      return item.formatted_item_total;
    }
    if (item.package?.aggregated_cent != null) {
      return MoneyUtils.formatCent(item.package.aggregated_cent);
    }
    const amount = item.formatted_item_total ?? item.price;
    return MoneyUtils.formatNoCents(amount);
  }

  getPaymentStatus(booking: BookingInfo): string {
    if (!booking.amount_cent || booking.amount_cent === 0) return 'Betald';
    if (booking.paid_cent && booking.paid_cent >= booking.amount_cent) return 'Betald';
    // Open prebooking: paid at the venue, no online payment
    if (booking.pay_at_venue) return 'Betalas på plats';
    if (!booking.paid_cent || booking.paid_cent === 0) return 'Obetald';
    return 'Delbetald';
  }

  getPaymentStatusColor(booking: BookingInfo): string {
    const status = this.getPaymentStatus(booking);
    if (status === 'Betald') return 'text-green-600';
    if (status === 'Betalas på plats') return 'text-theme-text';
    if (status === 'Delbetald') return 'text-yellow-600';
    return 'text-red-600';
  }

  payBooking(booking: BookingInfo): void {
    this.authorizedApi.createBookingReceipt(this.venueId, booking.booking_ref).subscribe(res => {
      this.router.navigate(['/', this.venueId, 'livesplit', res.receipt_key]);
    });
  }

  openReceipt(receiptKey: string): void {
    this.router.navigate(['/', this.venueId, 'receipt', receiptKey]);
  }

  formatDate(date: string | null): string {
    if (!date) return '';
    // Strip seconds from "2024-01-20 07:29:00" → "2024-01-20 07:29"
    return date.replace(/:\d{2}$/, '');
  }

  getCollectedDataEntries(booking: BookingInfo): {key: string, name: string, value: string}[] {
    if (!booking.collected_data) return [];
    return Object.entries(booking.collected_data).map(([key, data]) => ({
      key,
      name: data.name,
      value: data.value
    }));
  }
}
